import React, { useRef, useEffect, useState } from 'react';
import {
  FaPlus,
  FaSave,
  FaEdit,
  FaTrash,
  FaTimes,
  FaArrowLeft
} from 'react-icons/fa';

const ActionButtonGroup = ({
  handleAddOne,
  addOneButtonEnabled,
  handleSaveOrUpdate,
  saveButtonEnabled,
  isEditMode,
  handleEdit,
  editButtonEnabled,
  handleDelete,
  deleteButtonEnabled,
  handleCancel,
  cancelButtonEnabled,
  handleBack,
  backButtonEnabled,
  permissions,
  nextTabIndex,
  component,
  isDeleted,
}) => {
  const editButtonRef = useRef(null);
  const updateButtonRef = useRef(null);
  const resaleMillDropdownRef = useRef(null);
  const [focusedButton, setFocusedButton] = useState(null);

  useEffect(() => {
    if (editButtonEnabled && editButtonRef.current) {
      editButtonRef.current.focus();
    }
  }, [editButtonEnabled]);

  useEffect(() => {
    if (isEditMode && updateButtonRef.current) {
      updateButtonRef.current.focus();
    }
  }, [isEditMode]);

  const handleKeyDown = (event, handler) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handler();
      if (handler === handleAddOne || handler === handleEdit) {
        if (resaleMillDropdownRef.current) {
          resaleMillDropdownRef.current.focus();
        }
      } else if (handler === handleCancel) {
        editButtonRef.current.focus();
      } else if (handler === handleEdit) {
        updateButtonRef.current.focus();
      }
    }
  };

  const getButtonStyle = (enabled, permission, buttonKey) => {
    const isEnabled = enabled && permission !== "N";
    const isFocused = focusedButton === buttonKey;

    return {
      backgroundColor: isFocused
        ? "#FFEE58"
        : isEnabled
          ? "#1976d2"
          : "#e0e0e0",
      color: isFocused
        ? "#333"
        : isEnabled
          ? "#fff"
          : "#888",
      border: "none",
      borderRadius: "4px",
      cursor: isEnabled ? "pointer" : "not-allowed",
      minWidth: "80px",
      height: "32px",
      fontSize: "13px",
      fontWeight: "500",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
      padding: "0 10px",
      boxShadow: isFocused ? "0px 2px 6px rgba(0, 0, 0, 0.15)" : "none",
      transform: isFocused ? "scale(1.02)" : "scale(1)",
      transition: "all 0.2s ease-in-out",
      outline: "none",
    };
  };

  const handleCancelClick = () => {
    handleCancel();
    setTimeout(() => {
      if (editButtonRef.current) {
        editButtonRef.current.focus();
        setFocusedButton("edit");
      }
    }, 0);
  };

  return (
    <div
      style={{
        marginTop: "-20px",
        marginBottom: "10px",
        display: "flex",
        gap: "1px",
        flexWrap: "wrap",
      }}
    >
      <button
        onClick={handleAddOne}
        disabled={!addOneButtonEnabled || permissions?.canSave === "N"}
        onKeyDown={(event) => handleKeyDown(event, handleAddOne)}
        onFocus={() => setFocusedButton("add")}
        onBlur={() => setFocusedButton(null)}
        style={getButtonStyle(addOneButtonEnabled, permissions?.canSave, "add")}
        tabIndex={nextTabIndex}
      >
        <FaPlus /> Add
      </button>

      {isEditMode ? (
        <button
          ref={updateButtonRef}
          onClick={handleSaveOrUpdate}
          onKeyDown={(event) => handleKeyDown(event, handleSaveOrUpdate)}
          id="update"
          type='submit'
          onFocus={() => setFocusedButton("update")}
          onBlur={() => setFocusedButton(null)}
          style={getButtonStyle(true, "Y", "update")}
          tabIndex={nextTabIndex}
        >
          <FaSave /> Update
        </button>
      ) : (
        <button
          onClick={handleSaveOrUpdate}
          disabled={isDeleted || !saveButtonEnabled || permissions?.canSave === "N"}
          onKeyDown={(event) => handleKeyDown(event, handleSaveOrUpdate)}
          id="save"
          type='submit'
          onFocus={() => setFocusedButton("save")}
          onBlur={() => setFocusedButton(null)}
          style={getButtonStyle(saveButtonEnabled, permissions?.canSave, "save")}
          tabIndex={nextTabIndex}
        >
          <FaSave /> Save
        </button>
      )}

      <button
        ref={editButtonRef}
        onClick={handleEdit}
        disabled={isDeleted || !editButtonEnabled || permissions?.canEdit === "N"}
        onKeyDown={(event) => handleKeyDown(event, handleEdit)}
        onFocus={() => setFocusedButton("edit")}
        onBlur={() => setFocusedButton(null)}
        style={getButtonStyle(editButtonEnabled, permissions?.canEdit, "edit")}
      >
        <FaEdit /> Edit
      </button>

      <button
        onClick={handleDelete}
        disabled={isDeleted || !deleteButtonEnabled || permissions?.canDelete === "N"}
        onKeyDown={(event) => handleKeyDown(event, handleDelete)}
        onFocus={() => setFocusedButton("delete")}
        onBlur={() => setFocusedButton(null)}
        style={getButtonStyle(deleteButtonEnabled, permissions?.canDelete, "delete")}
        tabIndex={nextTabIndex}
      >
        <FaTrash /> Delete
      </button>

      <button
        onClick={handleCancelClick}
        disabled={isDeleted || !cancelButtonEnabled}
        onKeyDown={(event) => handleKeyDown(event, handleCancelClick)}
        onFocus={() => setFocusedButton("cancel")}
        onBlur={() => setFocusedButton(null)}
        style={getButtonStyle(cancelButtonEnabled, "Y", "cancel")}
        tabIndex={nextTabIndex}
      >
        <FaTimes /> Cancel
      </button>

      <button
        onClick={handleBack}
        disabled={!backButtonEnabled}
        onKeyDown={(event) => handleKeyDown(event, handleBack)}
        onFocus={() => setFocusedButton("back")}
        onBlur={() => setFocusedButton(null)}
        style={getButtonStyle(backButtonEnabled, "Y", "back")}
        tabIndex={nextTabIndex}
      >
        <FaArrowLeft /> Back
      </button>

      {component}
    </div>
  );
};

export default ActionButtonGroup;

